import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Autorenew as AutorenewIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useMemo, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent, SyntheticEvent } from "react";
import { useNavigate } from "react-router";
import {
  personalityPerksMap,
  personalityPitfallsMap,
  personalityStrengthsMap,
  quizColorMap,
} from "../../../components/personality-quiz/utils";
import { PersonalityType } from "../../../__generated__/types";
import { PeronalityTriangle } from "../../../components/personality-quiz/triangle";
import {
  ACCENT_COLOR,
  DESIGNER_INTERACTION_STYLE,
  DESIGNER_PERSONALITY_EXAMPLE,
  FIRST_TAB_TITLE_SIZE,
  WORKING_TABS,
  type WorkingTab,
} from "../constants";
import {
  PERSONALITY_PRIORITY_DETAILS,
  PERSONALITY_PRIORITY_MAP,
  TOP_TIPS_BY_COLOR,
  WORKING_BY_COLOR_STYLE_TEXT,
  WORKING_BY_COLOR_STYLE_TIPS,
  WORKING_TOGETHER_COPY,
  WORKING_WITH_EXTROVERT_CARDS,
  WORKING_WITH_INTROVERT_CARDS,
} from "../data";
import {
  formatPersonalityType,
  getInteractionSliderValues,
  normalizePersonalityEnum,
  normalizePersonalityKey,
} from "../utils";
import {
  getCompatibilityLabel,
  getCompatibilityRatingFromMatchScore,
  getCompatibilityStars,
} from "../../matches-page/utils";
import { RATE_ICON_SRC } from "../../matches-page/constants";
import { Pages } from "../../../utils";

const TraitColumn = ({
  label,
  items,
  isPlaceholder = false,
  placeholderCount = 4,
}: {
  label: string;
  items: string[];
  isPlaceholder?: boolean;
  placeholderCount?: number;
}) => (
  <Box>
    <Typography
      sx={{
        fontWeight: 700,
        color: ACCENT_COLOR,
        fontSize: "1.25rem",
        pb: 0.5,
        mb: 2,
        borderBottom: "1px solid #E5E7EB",
        width: "100%",
      }}
    >
      {label}
    </Typography>
    <Stack spacing={1}>
      {(isPlaceholder
        ? Array.from({ length: placeholderCount }, (_item, index) => `placeholder-${index}`)
        : items
      ).map((item, index) => (
        <Stack
          key={`${label}-${isPlaceholder ? "placeholder" : item}-${index}`}
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <Box
            sx={{
              width: 0,
              height: 0,
              borderTop: "6px solid transparent",
              borderBottom: "6px solid transparent",
              borderLeft: `10px solid ${isPlaceholder ? "#E5E7EB" : "#10B981"}`,
            }}
          />
          {isPlaceholder ? (
            <Skeleton
              variant="text"
              height={20}
              width={`${70 + (index % 3) * 8}%`}
            />
          ) : (
            <Typography sx={{ color: "#4B5563" }}>{item}</Typography>
          )}
        </Stack>
      ))}
    </Stack>
  </Box>
);

const WorkingWithFlipCard = ({
  front,
  back,
}: {
  front: string;
  back: string;
}) => {
  const [flipped, setFlipped] = useState(false);
  const lastPointerType = useRef<string | null>(null);
  const toggleFlip = () => setFlipped((prev) => !prev);
  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    lastPointerType.current = event.pointerType;
  };
  const handleClick = () => {
    if (lastPointerType.current && lastPointerType.current !== "mouse") {
      toggleFlip();
    }
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleFlip();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      aria-label={`Flip card: ${front}`}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      sx={{
        width: "100%",
        aspectRatio: "1 / 1",
        perspective: "1200px",
        cursor: "pointer",
        outline: "none",
        "&:focus-visible .flip-card-inner": {
          boxShadow: "0 0 0 2px rgba(79, 70, 229, 0.6)",
          borderRadius: "16px",
        },
      }}
    >
      <Box
        className="flip-card-inner"
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          transition: "transform 0.6s ease",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        <Card
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            border: "1px solid #E5E7EB",
            borderRadius: "16px",
            boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.08)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          <CardContent
            sx={{
              position: "relative",
              p: 3,
              pt: 4,
              height: "100%",
            }}
          >
            <AutorenewIcon
              aria-hidden="true"
              sx={{
                color: "#E5E7EB",
                fontSize: 18,
                position: "absolute",
                top: 12,
                right: 12,
              }}
            />
            <Typography sx={{ color: "#4B5563", fontSize: "0.95rem" }}>
              {front}
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            border: "1px solid #E5E7EB",
            borderRadius: "16px",
            boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.08)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            backgroundColor: "#fff",
          }}
        >
          <CardContent
            sx={{
              position: "relative",
              p: 3,
              pt: 4,
              height: "100%",
            }}
          >
            <AutorenewIcon
              aria-hidden="true"
              sx={{
                color: "#E5E7EB",
                fontSize: 18,
                position: "absolute",
                top: 12,
                right: 12,
              }}
            />
            <Typography sx={{ color: "#4B5563", fontSize: "0.9rem", lineHeight: 1.5 }}>
              {back}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const WorkingWithStaticCard = ({ text }: { text: string }) => (
  <Card
    sx={{
      border: "1px solid #E5E7EB",
      borderRadius: "16px",
      boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.08)",
      height: "100%",
      minHeight: 160,
      display: "flex",
      alignItems: "flex-start",
    }}
  >
    <CardContent
      sx={{
        p: 2.25,
        width: "100%",
      }}
    >
      <Typography sx={{ color: "#4B5563", fontSize: "0.9rem", lineHeight: 1.5 }}>
        {text}
      </Typography>
    </CardContent>
  </Card>
);

type PersonalityTabProps = {
  personalityType: string;
  profilePersonalityType?: string | null;
  designerPersonalityType?: string | null;
  viewerRole?: "designer" | "homeowner";
  displayMode?: "full" | "compatibility";
  matchScore?: number | null;
  showHeaderCompatibilityRating?: boolean;
  viewerLabelOverride?: string;
  counterpartLabelOverride?: string;
  traitPrimaryLabelOverride?: string;
  traitSecondaryLabelOverride?: string;
};

const CompatibilityStars = ({ rating }: { rating: number | null }) => {
  const stars = getCompatibilityStars(rating);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {stars.map((state, index) => (
        <Box
          key={`${state}-${index}`}
          component="img"
          src={RATE_ICON_SRC[state]}
          alt={`${state} star`}
          sx={{ width: 22, height: 22, display: "block" }}
        />
      ))}
    </Stack>
  );
};

const AwaitingMatchPlaceholder = ({
  title,
  copy,
  onViewMatches,
}: {
  title: string;
  copy: string;
  onViewMatches: () => void;
}) => {
  const titleWords = title.split(" ").filter(Boolean);
  const titleLines =
    titleWords.length > 3
      ? [titleWords[0], titleWords[1], titleWords.slice(2).join(" ")]
      : titleWords;

  return (
    <Stack spacing={1.5} alignItems="center">
      <Box sx={{ width: 180, height: 140, position: "relative" }}>
        <Box
          component="svg"
          viewBox="0 0 421 315"
          sx={{ width: "100%", height: "100%", display: "block" }}
          aria-hidden="true"
        >
          <polygon
            fill="none"
            stroke="#D1D5DB"
            strokeWidth="2"
            strokeLinejoin="round"
            points="210.5 .2 392.2 314.8 28.8 314.8 210.5 .2"
          />
          <line
            x1="149.9"
            y1="105.2"
            x2="271.1"
            y2="105.2"
            stroke="#D1D5DB"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: 26,
            right: 26,
            top: 52,
            bottom: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              color: "#6B7280",
              fontFamily: '"Jost", sans-serif',
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "0px",
              whiteSpace: "pre-line",
            }}
          >
            {titleLines.join("\n")}
          </Box>
        </Box>
      </Box>
    <Typography
      sx={{
        color: "#9CA3AF",
        fontSize: "0.85rem",
        textAlign: "center",
        lineHeight: 1.6,
        maxWidth: 220,
      }}
    >
      {copy}
    </Typography>
    <Button
      variant="outlined"
      onClick={onViewMatches}
      sx={{
        textTransform: "none",
        borderRadius: "12px",
        borderColor: "#6D63FF",
        color: "#6D63FF",
        fontWeight: 600,
        px: 3,
        "&:hover": {
          borderColor: "#5B53E6",
          backgroundColor: "rgba(109, 99, 255, 0.08)",
        },
      }}
    >
      View Matches
    </Button>
    </Stack>
  );
};

const EMPTY_TRIANGLE_COLORS = ["#E5E7EB", "#E5E7EB", "#E5E7EB"] as const;

const PersonalityTab = ({
  personalityType,
  profilePersonalityType,
  designerPersonalityType,
  viewerRole = "designer",
  displayMode = "full",
  matchScore = null,
  showHeaderCompatibilityRating = true,
  viewerLabelOverride,
  counterpartLabelOverride,
  traitPrimaryLabelOverride,
  traitSecondaryLabelOverride,
}: PersonalityTabProps) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [workingTab, setWorkingTab] = useState(0);
  const viewerIsHomeowner = viewerRole === "homeowner";
  const hasDesignerData =
    typeof designerPersonalityType === "string" && designerPersonalityType.trim().length > 0;
  const showDesignerDetails = !viewerIsHomeowner || hasDesignerData;
  const showDesignerPlaceholder = viewerIsHomeowner && !hasDesignerData;
  const isCompatibilityMode = displayMode === "compatibility";
  const containerMaxWidth = isCompatibilityMode ? 960 : 744;
  const viewerLabel =
    viewerLabelOverride ??
    (viewerIsHomeowner && isCompatibilityMode ? "You" : viewerIsHomeowner ? "Homeowner" : "You");
  const counterpartLabel =
    counterpartLabelOverride ?? (viewerIsHomeowner ? "Designer" : "Homeowner");
  const primaryTraitLabel = viewerIsHomeowner ? "Homeowner" : "Designer";
  const secondaryTraitLabel = viewerIsHomeowner ? "Designer" : "Homeowner";
  const traitPrimaryLabel =
    traitPrimaryLabelOverride ??
    (viewerIsHomeowner && isCompatibilityMode ? "You" : primaryTraitLabel);
  const traitSecondaryLabel =
    traitSecondaryLabelOverride ??
    (viewerIsHomeowner && isCompatibilityMode ? "Designer" : secondaryTraitLabel);
  const awaitingMatchLabel = `Awaiting ${counterpartLabel} Match`;
  const awaitingMatchCopy = `Your ${counterpartLabel.toLowerCase()}'s personality will appear here. View your ${counterpartLabel} Matches to see how you both compare.`;

  const workingTabLabel =
    (WORKING_TABS[workingTab] ?? WORKING_TABS[0]) as WorkingTab;
  const workingTogetherCopy = useMemo(() => {
    const key = normalizePersonalityKey(profilePersonalityType ?? personalityType);
    return WORKING_TOGETHER_COPY[key] ?? WORKING_TOGETHER_COPY.extrovertblueorange;
  }, [profilePersonalityType, personalityType]);
  const workingContent = workingTogetherCopy[workingTabLabel] ?? "";
  const firstPersonalityColor = useMemo(() => {
    const parts = personalityType.split(" ").filter(Boolean);
    return parts.length >= 3 ? parts[1] : "Blue";
  }, [personalityType]);
  const homeownerInteractionSliders = useMemo(
    () => getInteractionSliderValues(profilePersonalityType ?? personalityType),
    [profilePersonalityType, personalityType]
  );
  const designerInteractionSliders = useMemo(
    () =>
      getInteractionSliderValues(
        showDesignerDetails ? designerPersonalityType ?? DESIGNER_INTERACTION_STYLE : null
      ),
    [designerPersonalityType, showDesignerDetails]
  );
  const designerPersonalityKey = useMemo(() => {
    const normalized = normalizePersonalityEnum(designerPersonalityType);
    if (normalized) return normalized;
    return showDesignerDetails ? DESIGNER_PERSONALITY_EXAMPLE : null;
  }, [designerPersonalityType, showDesignerDetails]);
  const homeownerPersonalityKey =
    normalizePersonalityEnum(profilePersonalityType) ??
    PersonalityType.ExtrovertBlueOrange;
  const viewerPersonalityKey =
    (viewerIsHomeowner ? homeownerPersonalityKey : designerPersonalityKey) ??
    PersonalityType.ExtrovertBlueOrange;
  const counterpartPersonalityKey = viewerIsHomeowner
    ? designerPersonalityKey
    : homeownerPersonalityKey;
  const viewerStrengths = personalityStrengthsMap[viewerPersonalityKey] ?? [];
  const counterpartStrengths = counterpartPersonalityKey
    ? personalityStrengthsMap[counterpartPersonalityKey] ?? []
    : [];
  const viewerPerks = personalityPerksMap[viewerPersonalityKey] ?? [];
  const counterpartPerks = counterpartPersonalityKey
    ? personalityPerksMap[counterpartPersonalityKey] ?? []
    : [];
  const viewerPitfalls = personalityPitfallsMap[viewerPersonalityKey] ?? [];
  const counterpartPitfalls = counterpartPersonalityKey
    ? personalityPitfallsMap[counterpartPersonalityKey] ?? []
    : [];
  const viewerTriangleColors = useMemo(
    () => quizColorMap[viewerPersonalityKey] ?? [],
    [viewerPersonalityKey]
  );
  const counterpartTriangleColors = useMemo(
    () =>
      counterpartPersonalityKey ? quizColorMap[counterpartPersonalityKey] ?? [] : [],
    [counterpartPersonalityKey]
  );
  const priorityItems = useMemo(() => {
    const key = normalizePersonalityKey(profilePersonalityType ?? personalityType);
    return (
      PERSONALITY_PRIORITY_MAP[key] ??
      PERSONALITY_PRIORITY_MAP.extrovertblueorange
    );
  }, [profilePersonalityType, personalityType]);
  const priorityDetails = useMemo(() => {
    const key = normalizePersonalityKey(profilePersonalityType ?? personalityType);
    return (
      PERSONALITY_PRIORITY_DETAILS[key] ??
      PERSONALITY_PRIORITY_DETAILS.extrovertblueorange
    );
  }, [profilePersonalityType, personalityType]);
  const workingByColorStyleText = useMemo(() => {
    const key = normalizePersonalityKey(profilePersonalityType ?? personalityType);
    return (
      WORKING_BY_COLOR_STYLE_TEXT[key] ??
      WORKING_BY_COLOR_STYLE_TEXT.extrovertblueorange
    );
  }, [profilePersonalityType, personalityType]);
  const workingByColorStyleTips = useMemo(() => {
    type TipKey = keyof typeof WORKING_BY_COLOR_STYLE_TIPS;
    const key = firstPersonalityColor as TipKey;
    return WORKING_BY_COLOR_STYLE_TIPS[key] ?? WORKING_BY_COLOR_STYLE_TIPS.Blue;
  }, [firstPersonalityColor]);
  const workingByColorStyleTipsText = useMemo(
    () => workingByColorStyleTips.join(" "),
    [workingByColorStyleTips]
  );
  const workingWithPersonalityKey = useMemo(
    () => normalizePersonalityKey(profilePersonalityType ?? personalityType),
    [profilePersonalityType, personalityType]
  );
  const isIntrovertStyle = workingWithPersonalityKey.includes("introvert");
  const workingWithLabel = isIntrovertStyle ? "Introvert" : "Extrovert";
  const workingWithCards = isIntrovertStyle
    ? WORKING_WITH_INTROVERT_CARDS
    : WORKING_WITH_EXTROVERT_CARDS;
  const mobileWorkingWithCards = useMemo(
    () =>
      workingWithCards.flatMap((item, index) => [
        {
          key: `${item.front}-front-${index}`,
          text: item.front,
        },
        {
          key: `${item.front}-back-${index}`,
          text: item.back,
        },
      ]),
    [workingWithCards]
  );
  const topTipsForColor = useMemo(() => {
    type TipKey = keyof typeof TOP_TIPS_BY_COLOR;
    const key = firstPersonalityColor as TipKey;
    return TOP_TIPS_BY_COLOR[key] ?? TOP_TIPS_BY_COLOR.Blue;
  }, [firstPersonalityColor]);

  const compatibilityRating = useMemo(
    () => getCompatibilityRatingFromMatchScore(matchScore),
    [matchScore]
  );
  const compatibilityLabel = getCompatibilityLabel(compatibilityRating);
  const homeownerInteractionStyle = personalityType;
  const designerInteractionStyle = showDesignerDetails
    ? formatPersonalityType(designerPersonalityType ?? DESIGNER_INTERACTION_STYLE)
    : "";
  const viewerInteractionStyle = viewerIsHomeowner
    ? homeownerInteractionStyle
    : designerInteractionStyle;
  const counterpartInteractionStyle = viewerIsHomeowner
    ? showDesignerDetails
      ? designerInteractionStyle
      : ""
    : homeownerInteractionStyle;
  const viewerInteractionValues = viewerIsHomeowner
    ? homeownerInteractionSliders
    : designerInteractionSliders;
  const counterpartInteractionValues = viewerIsHomeowner
    ? designerInteractionSliders
    : homeownerInteractionSliders;
  const personalityCompatibilityCopy = showDesignerDetails
    ? `When ${viewerInteractionStyle} and ${counterpartInteractionStyle} work together, they make a creative and caring team. They are different in specific ways, but those differences can help them work better together if they choose to focus on the positive.`
    : "";
  const workingTogetherSections = useMemo(() => {
    if (!isCompatibilityMode) return [];
    const sentences = workingContent
      .split(/(?<=\.)\s+/)
      .map((sentence) => sentence.trim())
      .filter(Boolean);
    const sections = [
      {
        label: "Two Types Talking",
        text: sentences.slice(0, 2).join(" "),
      },
      {
        label: "Talk Troubles",
        text: sentences.slice(2, 4).join(" "),
      },
      {
        label: "Talk Tactics",
        text: sentences.slice(4).join(" "),
      },
    ];
    return sections.filter((section) => section.text);
  }, [isCompatibilityMode, workingContent]);

  const counterpartTrianglePalette = showDesignerDetails
    ? counterpartTriangleColors
    : EMPTY_TRIANGLE_COLORS;
  const showCounterpartTraits = showDesignerDetails || showDesignerPlaceholder;

  return (
    <Stack
      spacing={{ xs: 4, md: 8 }}
      sx={{
        pt: { xs: 2, md: 3 },
        px: { xs: 2, md: 0 },
        maxWidth: containerMaxWidth,
        mx: "auto",
      }}
    >
      {!isCompatibilityMode && (
        <Typography
          align="center"
          sx={{
            color: "#6B7280",
            fontSize: "0.95rem",
          }}
        >
          {viewerIsHomeowner
            ? "Understand the designer communication, involvement, and collaboration style."
            : "Understand the homeowner communication, involvement, and collaboration style."}
        </Typography>
      )}

      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Stack spacing={0.5} alignItems="flex-start">
            <Typography
              sx={{
                fontWeight: 700,
                color: ACCENT_COLOR,
                fontSize: FIRST_TAB_TITLE_SIZE,
              }}
            >
              Interaction Style
            </Typography>
            <Typography variant="caption" sx={{ color: "#9CA3AF", textAlign: "left" }}>
              Powered by the experts at ColorWorks Group
            </Typography>
          </Stack>
          {isCompatibilityMode && showHeaderCompatibilityRating && (
            <Stack spacing={0.5} alignItems="flex-end">
              <Typography sx={{ fontWeight: 600, color: "#111827" }}>
                Compatibility Rating
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <CompatibilityStars rating={compatibilityRating} />
                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                  {compatibilityRating !== null
                    ? `(${compatibilityRating.toFixed(1)})${compatibilityLabel ? ` ${compatibilityLabel}` : ""}`
                    : ""}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>

        <Grid container spacing={3} sx={{ mt: 3.5 }} alignItems="center">
          <Grid item xs={12} md={4}>
            <Stack spacing={1.5} alignItems="center">
              <Typography sx={{ fontWeight: 600, color: "#6B7280" }}>
                {viewerLabel}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: ACCENT_COLOR }}>
                {viewerInteractionStyle}
              </Typography>
              <Box sx={{ width: 180, display: "flex", justifyContent: "center" }}>
                <PeronalityTriangle
                  color1={viewerTriangleColors[0] || "#000"}
                  color2={viewerTriangleColors[1] || "#000"}
                  color3={viewerTriangleColors[2] || "#000"}
                />
              </Box>
              <Stack spacing={1.5} sx={{ width: 180 }}>
                {[
                  {
                    gradient:
                      "linear-gradient(90deg, #C4B5FD 0%, #6D63FF 100%)",
                    value: viewerInteractionValues.ie,
                    left: "I",
                    right: "E",
                  },
                  {
                    gradient:
                      "linear-gradient(90deg, #F97316 0%, #FBBF24 100%)",
                    value: viewerInteractionValues.og,
                    left: "O",
                    right: "G",
                  },
                  {
                    gradient:
                      "linear-gradient(90deg, #10B981 0%, #1D4ED8 100%)",
                    value: viewerInteractionValues.grb,
                    left: "Gr",
                    right: "B",
                  },
                ].map((slider) => (
                  <Box key={slider.left}>
                    <Box
                      sx={{
                        position: "relative",
                        height: 8,
                        borderRadius: 999,
                        background: slider.gradient,
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: -4,
                          left: `calc(${slider.value}% - 8px)`,
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                          border: "1px solid #D1D5DB",
                          boxShadow: "0px 4px 10px rgba(15, 23, 42, 0.16)",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.75rem",
                        color: "#6B7280",
                        mt: 0.4,
                      }}
                    >
                      <span>{slider.left}</span>
                      <span>{slider.right}</span>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1.25} alignItems="center">
              {compatibilityRating === null ? (
                <>
                  <Typography sx={{ fontWeight: 700, fontSize: "2.5rem", color: "#111827" }}>
                    0
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>Compatibility Rating</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      component="img"
                      src={RATE_ICON_SRC.empty}
                      alt="No rating"
                      sx={{ width: 22, height: 22, opacity: 0.6 }}
                    />
                    <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                      (0) No Rating Available
                    </Typography>
                  </Stack>
                </>
              ) : (
                <>
                  <Typography sx={{ fontWeight: 700, fontSize: "2rem", color: "#111827" }}>
                    {compatibilityRating.toFixed(1)}
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>Compatibility Rating</Typography>
                  <Stack spacing={0.5} alignItems="center">
                    <CompatibilityStars rating={compatibilityRating} />
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {`(${compatibilityRating.toFixed(1)})${compatibilityLabel ? ` ${compatibilityLabel}` : ""}`}
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack
              spacing={1.5}
              alignItems="center"
              sx={{ mt: counterpartLabel === "Designer" ? "40px" : 0 }}
            >
              <Typography sx={{ fontWeight: 600, color: "#6B7280" }}>
                {counterpartLabel}
              </Typography>
              {counterpartLabel === "Designer" && !showDesignerDetails && (
                <Typography sx={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
                  Designer's Personality
                </Typography>
              )}
              {showDesignerDetails ? (
                <>
                  <Typography sx={{ fontWeight: 700, color: ACCENT_COLOR }}>
                    {counterpartInteractionStyle}
                  </Typography>
                  <Box sx={{ width: 180, display: "flex", justifyContent: "center" }}>
                    <PeronalityTriangle
                      color1={counterpartTrianglePalette[0] || "#000"}
                      color2={counterpartTrianglePalette[1] || "#000"}
                      color3={counterpartTrianglePalette[2] || "#000"}
                    />
                  </Box>
                  <Stack spacing={1.5} sx={{ width: 180 }}>
                    {[
                      {
                        gradient:
                          "linear-gradient(90deg, #C4B5FD 0%, #6D63FF 100%)",
                        value: counterpartInteractionValues.ie,
                        left: "I",
                        right: "E",
                      },
                      {
                        gradient:
                          "linear-gradient(90deg, #F97316 0%, #FBBF24 100%)",
                        value: counterpartInteractionValues.og,
                        left: "O",
                        right: "G",
                      },
                      {
                        gradient:
                          "linear-gradient(90deg, #10B981 0%, #1D4ED8 100%)",
                        value: counterpartInteractionValues.grb,
                        left: "Gr",
                        right: "B",
                      },
                    ].map((slider) => (
                      <Box key={slider.left}>
                        <Box
                          sx={{
                            position: "relative",
                            height: 8,
                            borderRadius: 999,
                            background: slider.gradient,
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: -4,
                              left: `calc(${slider.value}% - 8px)`,
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              border: "1px solid #D1D5DB",
                              boxShadow: "0px 4px 10px rgba(15, 23, 42, 0.16)",
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "0.75rem",
                            color: "#6B7280",
                            mt: 0.4,
                          }}
                        >
                          <span>{slider.left}</span>
                          <span>{slider.right}</span>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </>
              ) : (
                <AwaitingMatchPlaceholder
                  title={awaitingMatchLabel}
                  copy={awaitingMatchCopy}
                  onViewMatches={() => navigate(Pages.MATCHES)}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {isCompatibilityMode ? (
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: ACCENT_COLOR,
              fontSize: FIRST_TAB_TITLE_SIZE,
            }}
          >
            Personality Compatibility
          </Typography>
          {showDesignerDetails ? (
            <Typography sx={{ mt: 3, color: "#6B7280", lineHeight: 1.7 }}>
              {personalityCompatibilityCopy}
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 3 }}>
              <Skeleton variant="text" width="90%" height={24} />
              <Skeleton variant="text" width="85%" height={24} />
              <Skeleton variant="text" width="70%" height={24} />
            </Stack>
          )}
        </Box>
      ) : (
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: ACCENT_COLOR,
              fontSize: FIRST_TAB_TITLE_SIZE,
            }}
          >
            Top Priorities For A {firstPersonalityColor}
          </Typography>
          <Stack
            direction="row"
            spacing={3}
            sx={{ mt: { xs: 4, md: 8 }, flexWrap: "wrap" }}
          >
            {priorityItems.map((item) => (
              <Stack
                key={item}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 0,
                    height: 0,
                    borderTop: "6px solid transparent",
                    borderBottom: "6px solid transparent",
                    borderLeft: "10px solid #10B981",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    color: "#111827",
                    fontSize: "0.9rem",
                  }}
                >
                  {item.toUpperCase()}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Typography
            sx={{
              mt: 3,
              color: "#6B7280",
              lineHeight: 1.7,
            }}
          >
            {priorityDetails}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography
          sx={{
            fontWeight: 700,
            color: ACCENT_COLOR,
            fontSize: FIRST_TAB_TITLE_SIZE,
          }}
        >
          Working Together
        </Typography>
        <Tabs
          value={workingTab}
          onChange={(_event: SyntheticEvent, value: number) =>
            setWorkingTab(value)
          }
          sx={{
            mt: { xs: 4, md: 8 },
            borderBottom: "1px solid #D7D3F0",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              color: "#6B7280",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#6D63FF",
            },
          }}
          TabIndicatorProps={{
            style: {
              backgroundColor: "#6D63FF",
              height: 3,
            },
          }}
        >
          {WORKING_TABS.map((tab) => (
            <Tab key={tab} label={tab} />
          ))}
        </Tabs>
        {isCompatibilityMode ? (
          <Stack spacing={2} sx={{ mt: 2, color: "#6B7280" }}>
            {workingTogetherSections.map((section) => (
              <Typography key={section.label} sx={{ lineHeight: 1.7 }}>
                <Box component="span" sx={{ fontWeight: 700, color: "#374151" }}>
                  {section.label}:{" "}
                </Box>
                {section.text}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Typography
            sx={{
              color: "#6B7280",
              mt: 2,
              lineHeight: 1.7,
            }}
          >
            {workingContent}
          </Typography>
        )}
      </Box>

      <Stack spacing={2}>
        {[
          {
            title: "Strengths",
            viewerItems: viewerStrengths,
            counterpartItems: counterpartStrengths,
            defaultExpanded: true,
          },
          {
            title: "Perks",
            viewerItems: viewerPerks,
            counterpartItems: counterpartPerks,
          },
          {
            title: "Pitfalls",
            viewerItems: viewerPitfalls,
            counterpartItems: counterpartPitfalls,
          },
        ].map((section) => (
          <Accordion
            key={section.title}
            defaultExpanded={section.defaultExpanded}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid #D7D3F0",
              borderRadius: 2,
              overflow: "hidden",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: "45px",
                pt: "20px",
                pl: "30px",
                "& .MuiAccordionSummary-content": {
                  fontWeight: 700,
                  color: "#4B5563",
                  fontSize: "1.25rem",
                },
              }}
            >
              {section.title}
            </AccordionSummary>
            <AccordionDetails sx={{ px: "45px", pb: "45px", pt: "40px" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={showCounterpartTraits ? 6 : 12}>
                  <TraitColumn label={traitPrimaryLabel} items={section.viewerItems} />
                </Grid>
                {showCounterpartTraits && (
                  <Grid item xs={12} md={6}>
                    <TraitColumn
                      label={traitSecondaryLabel}
                      items={section.counterpartItems}
                      isPlaceholder={showDesignerPlaceholder}
                      placeholderCount={Math.max(3, section.viewerItems.length)}
                    />
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {!isCompatibilityMode && (
        <>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: ACCENT_COLOR,
                fontSize: FIRST_TAB_TITLE_SIZE,
              }}
            >
              Working By Color Style
            </Typography>
          <Stack spacing={2} sx={{ mt: { xs: 4, md: 8 }, color: "#6B7280" }}>
              <Typography>{workingByColorStyleText}</Typography>
              <Typography sx={{ lineHeight: 1.7 }}>
                {workingByColorStyleTipsText}
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: ACCENT_COLOR,
                fontSize: FIRST_TAB_TITLE_SIZE,
              }}
            >
              {`Working With an ${workingWithLabel}`}
            </Typography>
            <Grid container spacing={2} sx={{ mt: { xs: 4, md: 8 } }}>
              {isMobile
                ? mobileWorkingWithCards.map((item) => (
                    <Grid item xs={6} key={item.key}>
                      <WorkingWithStaticCard text={item.text} />
                    </Grid>
                  ))
                : workingWithCards.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.front}>
                      <WorkingWithFlipCard front={item.front} back={item.back} />
                    </Grid>
                  ))}
            </Grid>
          </Box>

          <Box sx={{ pb: { xs: 6, md: "100px" } }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: ACCENT_COLOR,
                fontSize: FIRST_TAB_TITLE_SIZE,
              }}
            >
              {`Top Tips for Working with a ${firstPersonalityColor}`}
            </Typography>
            <Stack spacing={1.5} sx={{ mt: { xs: 4, md: 8 } }}>
              {topTipsForColor.map((tip) => (
                <Stack
                  key={`${firstPersonalityColor}-${tip}`}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 0,
                      height: 0,
                      borderTop: "6px solid transparent",
                      borderBottom: "6px solid transparent",
                      borderLeft: "10px solid #10B981",
                    }}
                  />
                  <Typography sx={{ color: "#4B5563" }}>{tip}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default PersonalityTab;
